import React, { useState, useEffect } from 'react';
import Modal from '../Modal';
import { Produit, ProduitCreate, Conditionnement } from '../../types/weighing';
import weighingService from '../../services/weighing';
import AddButton from '../AddButton';

interface EditProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (product: Partial<ProduitCreate>) => void;
  product: Produit;
}

const EditProductModal: React.FC<EditProductModalProps> = ({ isOpen, onClose, onSave, product }) => {
  const [formData, setFormData] = useState<ProduitCreate>({
    code: '',
    nom: '',
    type: 'ANACARDE',
    unite: 'kg',
    qualite_requise: true, // Par défaut, l'analyse qualité est requise
    description: '',
    categorie: '',
    prix_unitaire: 0,
    poids_moyen: 0,
    conditionnement: null,
    statut: 'actif'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [conditionnements, setConditionnements] = useState<Conditionnement[]>([]);
  const [loadingConditionnements, setLoadingConditionnements] = useState(false);
  const [showAddConditionnementModal, setShowAddConditionnementModal] = useState(false);
  const [newConditionnementData, setNewConditionnementData] = useState({
    nom: '',
    type: 'sac',
    capacite: 0,
    unite: 'kg',
    statut: 'actif'
  });

  useEffect(() => {
    if (product) {
      setFormData({
        code: product.code || '',
        nom: product.nom || '',
        type: product.type || 'ANACARDE',
        unite: product.unite || 'kg',
        qualite_requise: product.qualite_requise !== undefined ? product.qualite_requise : true, // Par défaut true si non défini
        description: product.description || '',
        categorie: product.categorie || '',
        prix_unitaire: parseFloat(product.prix_unitaire?.toString() || '0') || 0,
        poids_moyen: parseFloat(product.poids_moyen?.toString() || '0') || 0,
        conditionnement: product.conditionnement || null,
        statut: product.statut || 'actif'
      });
    }
  }, [product]);

  // Charger les conditionnements disponibles
  useEffect(() => {
    if (isOpen) {
      loadConditionnements();
    }
  }, [isOpen]);

  const loadConditionnements = async () => {
    setLoadingConditionnements(true);
    try {
      const response = await weighingService.getConditionnements();
      setConditionnements(response.results || []);
    } catch (error) {
      console.error('Erreur lors du chargement des conditionnements:', error);
    } finally {
      setLoadingConditionnements(false);
    }
  };

  const handleAddConditionnement = async () => {
    try {
      const response = await weighingService.createConditionnement(newConditionnementData);
      if (response.success) {
        await loadConditionnements();
        setShowAddConditionnementModal(false);
        setNewConditionnementData({
          nom: '',
          type: 'sac',
          capacite: 0,
          unite: 'kg',
          statut: 'actif'
        });
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout du conditionnement:', error);
    }
  };

  const handleNewConditionnementInputChange = (field: string, value: any) => {
    setNewConditionnementData(prev => ({ ...prev, [field]: value }));
  };

  const handleInputChange = (field: keyof ProduitCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nom?.trim()) {
      newErrors.nom = 'Le nom est requis';
    }
    // Description et poids_moyen ne sont plus obligatoires

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Modifier le produit" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Code et Nom */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Code produit
            </label>
            <input
              type="text"
              value={formData.code || ''}
              onChange={(e) => handleInputChange('code', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Ex: ANA1234"
              maxLength={20}
            />
            <p className="mt-1 text-xs text-gray-500">
              Code alphanumérique unique du produit
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom du produit <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nom}
              onChange={(e) => handleInputChange('nom', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.nom ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Ex: Riz blanc"
            />
            {errors.nom && (
              <p className="mt-1 text-sm text-red-600">{errors.nom}</p>
            )}
          </div>
        </div>

        {/* Type de produit */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type de produit 
          </label>
          <select
            value={formData.type}
            onChange={(e) => handleInputChange('type', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="ANACARDE">Anacarde</option>
            <option value="COTON">Coton</option>
            <option value="KARITE">Karité</option>
            <option value="ARACHIDE">Arachide</option>
            <option value="CAFE">Café</option>
            <option value="CACAO">Cacao</option>
            <option value="BLE_TENDRE">Blé tendre</option>
            <option value="MAIS">Maïs</option>
            <option value="ORGE">Orge</option>
            <option value="SOJA">Soja</option>
            <option value="TOURNESOL">Tournesol</option>
          </select>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="Description détaillée du produit (optionnelle)"
          />
        </div>

        {/* Unité et Contrôle analyse qualité */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unité
            </label>
            <select
              value={formData.unite}
              onChange={(e) => handleInputChange('unite', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="kg">Kilogrammes (kg)</option>
              <option value="g">Grammes (g)</option>
              <option value="l">Litres (l)</option>
              <option value="ml">Millilitres (ml)</option>
              <option value="pieces">Pièces</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contrôle analyse qualité
            </label>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900">Activer l'analyse qualité</h4>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.qualite_requise 
                    ? "L'étape d'analyse qualité sera obligatoire" 
                    : "L'étape d'analyse qualité sera sautée"
                  }
                </p>
              </div>
              <div className="ml-3">
                <button
                  type="button"
                  onClick={() => handleInputChange('qualite_requise', !formData.qualite_requise)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                    formData.qualite_requise ? 'bg-orange-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.qualite_requise ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Catégorie et Prix unitaire */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Catégorie
            </label>
            <input
              type="text"
              value={formData.categorie || ''}
              onChange={(e) => handleInputChange('categorie', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Ex: Céréales, Légumineuses"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prix unitaire (FCFA)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.prix_unitaire || ''}
              onChange={(e) => handleInputChange('prix_unitaire', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Poids moyen */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Poids moyen (kg)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.poids_moyen}
            onChange={(e) => handleInputChange('poids_moyen', parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="0.00 (optionnel)"
          />
        </div>

        {/* Conditionnement */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Conditionnement
          </label>
          <div className="flex gap-2">
            <select
              value={formData.conditionnement || ''}
              onChange={(e) => handleInputChange('conditionnement', e.target.value ? parseInt(e.target.value) : null)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              disabled={loadingConditionnements}
            >
              <option value="">Sélectionner un conditionnement</option>
              {conditionnements.map((cond) => (
                <option key={cond.id} value={cond.id}>
                  {cond.nom} - {cond.type_display} ({cond.capacite} {cond.unite_display})
                </option>
              ))}
            </select>
            <AddButton
              onClick={() => setShowAddConditionnementModal(true)}
              tooltip="Ajouter un nouveau conditionnement"
              className="flex-shrink-0"
            />
          </div>
          {loadingConditionnements && (
            <p className="mt-1 text-sm text-gray-500">Chargement des conditionnements...</p>
          )}
        </div>

        {/* Statut */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Statut
          </label>
          <select
            value={formData.statut}
            onChange={(e) => handleInputChange('statut', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="actif">Actif</option>
            <option value="inactif">Inactif</option>
          </select>
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Mettre à jour
          </button>
        </div>
      </form>

      {/* Modale pour ajouter un conditionnement */}
      {showAddConditionnementModal && (
        <Modal isOpen={showAddConditionnementModal} onClose={() => setShowAddConditionnementModal(false)} title="Ajouter un nouveau conditionnement" size="md">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du conditionnement 
              </label>
              <input
                type="text"
                value={newConditionnementData.nom}
                onChange={(e) => handleNewConditionnementInputChange('nom', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Ex: Sac de 50kg"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type 
                </label>
                <select
                  value={newConditionnementData.type}
                  onChange={(e) => handleNewConditionnementInputChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="sac">Sac</option>
                  <option value="carton">Carton</option>
                  <option value="bidon">Bidon</option>
                  <option value="palette">Palette</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Capacité
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newConditionnementData.capacite}
                  onChange={(e) => handleNewConditionnementInputChange('capacite', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unité
                </label>
                <select
                  value={newConditionnementData.unite}
                  onChange={(e) => handleNewConditionnementInputChange('unite', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="kg">Kilogrammes (kg)</option>
                  <option value="g">Grammes (g)</option>
                  <option value="l">Litres (l)</option>
                  <option value="ml">Millilitres (ml)</option>
                  <option value="pieces">Pièces</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statut
                </label>
                <select
                  value={newConditionnementData.statut}
                  onChange={(e) => handleNewConditionnementInputChange('statut', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="actif">Actif</option>
                  <option value="inactif">Inactif</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowAddConditionnementModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleAddConditionnement}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Ajouter le conditionnement
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  );
};

export default EditProductModal;
